import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { unwrapLookupItems } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { toDateTimeOffset } from '../utils/format';
import {
  DetailShell, Section, Grid, Icon, SplitButton,
  FieldText, FieldDate, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/vehicles/delivery-notes';

// Salt-okunur değer hücresi (FieldCustom + .dps-ff-input içinde input görünümü).
const ROVAL = { flex: 1, padding: '0 14px', fontSize: 14, fontWeight: 700, color: 'var(--dps-ink)' };

const STATUS_META = {
  DRAFT: { label: 'Taslak', tone: 'neutral' },
  PRINTED: { label: 'Basıldı', tone: 'wait' },
  IN_TRANSIT: { label: 'Yolda', tone: 'wait' },
  COMPLETED: { label: 'Tamamlandı', tone: 'ok' },
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function statusBadgeFor(status) {
  const meta = STATUS_META[status] || { label: status || '-', tone: 'neutral' };
  return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
}

function todayStr() {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
}

function emptyCreateForm() {
  return { warehouseId: '', date: todayStr(), documentDate: todayStr(), shipmentDate: todayStr(), description: '', matbuNo: '' };
}

const isVehicleWarehouse = (w) => {
  const t = String(w?.type ?? w?.Type ?? '').trim();
  return t.toLocaleLowerCase('tr-TR').startsWith('araç');
};

const escapeHtml = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

// Sevk İrsaliyesini ayrı bir pencerede yazdırılabilir biçimde açar. (Liste sayfasındaki ile aynı akış.)
function printDeliveryNote({ noteNo, warehouseName, warehouseCode, date, documentDate, shipmentDate, description, matbuNo, lines, totalCases, totalUnits }) {
  const rows = lines.map((l, i) => `
    <tr>
      <td style="text-align:center">${i + 1}</td>
      <td>${escapeHtml(l.productCode)}</td>
      <td>${escapeHtml(l.productName)}</td>
      <td style="text-align:right">${l.cases ?? 0}</td>
      <td style="text-align:right">${l.units ?? 0}</td>
    </tr>`).join('');
  const fmt = (v) => (v ? String(v).slice(0, 10) : '-');
  const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8" />
    <title>Sevk İrsaliyesi ${escapeHtml(noteNo || '')}</title>
    <style>
      body { font-family: Arial, Helvetica, sans-serif; color:#111; padding:24px; }
      h1 { font-size:20px; margin:0 0 4px; }
      .meta { font-size:13px; margin:12px 0 18px; display:grid; grid-template-columns:1fr 1fr; gap:4px 24px; }
      .meta b { display:inline-block; min-width:110px; }
      table { width:100%; border-collapse:collapse; font-size:13px; }
      th,td { border:1px solid #999; padding:6px 8px; }
      th { background:#f0f0f0; text-align:left; }
      tfoot td { font-weight:bold; }
      @media print { button { display:none; } }
    </style></head><body>
    <h1>Sevk İrsaliyesi</h1>
    <div class="meta">
      <div><b>İrsaliye No:</b> ${escapeHtml(noteNo || '-')}</div>
      <div><b>Araç Depo:</b> ${escapeHtml([warehouseCode, warehouseName].filter(Boolean).join(' - ') || '-')}</div>
      <div><b>Tarih:</b> ${escapeHtml(fmt(date))}</div>
      <div><b>Belge Tarihi:</b> ${escapeHtml(fmt(documentDate))}</div>
      <div><b>Sevk Tarihi:</b> ${escapeHtml(fmt(shipmentDate))}</div>
      <div><b>Matbu No:</b> ${escapeHtml(matbuNo || '-')}</div>
      <div style="grid-column:1 / -1"><b>Açıklama:</b> ${escapeHtml(description || '-')}</div>
    </div>
    <table>
      <thead><tr><th style="width:40px">#</th><th>Ürün Kodu</th><th>Ürün Adı</th><th style="width:90px;text-align:right">Koli</th><th style="width:90px;text-align:right">Adet</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="5" style="text-align:center">Kalem yok</td></tr>'}</tbody>
      <tfoot><tr><td colspan="3" style="text-align:right">Toplam</td><td style="text-align:right">${totalCases ?? 0}</td><td style="text-align:right">${totalUnits ?? 0}</td></tr></tfoot>
    </table>
    <p style="margin-top:24px"><button onclick="window.print()">Yazdır</button></p>
    </body></html>`;
  const w = window.open('', '_blank', 'width=900,height=700');
  if (!w) return;
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => { try { w.print(); } catch { /* kullanıcı manuel yazdırır */ } }, 300);
}

function formatDateTime(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
}

const EMPTY_DETAIL = {
  id: '', noteNo: '', plate: '', sdmName: '', status: '',
  deliveryDate: '', printedAt: null, totalCases: null, totalUnits: null,
};

export function DeliveryNoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete, canCopy } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Sevk İrsaliyesi' : 'Sevk İrsaliyesi Detayı', 'Araç deposundan sevk irsaliyesi oluşturma ve detay görüntüleme.');

  const [loading, setLoading] = useState(!isNew);
  const [toast, setToast] = useState(null);
  const [audit, setAudit] = useState(null);

  // ── Yeni Sevk İrsaliyesi (Araç Depo'dan) ──
  const [vehicleWarehouses, setVehicleWarehouses] = useState([]);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [snapshot, setSnapshot] = useState({ items: [], totalCases: 0, totalUnits: 0, warehouseName: '', warehouseCode: '' });
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  // ── Salt-okunur detay (mevcut kayıt) ──
  const [detail, setDetail] = useState(EMPTY_DETAIL);
  const [detailLines, setDetailLines] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const setCreate = (k, v) => setCreateForm((c) => ({ ...c, [k]: v }));

  // Yeni-modda araç depolarını yükle.
  const fetchVehicleWarehouses = useCallback(async () => {
    try {
      const res = await fetch('/api/warehouses/Select', { headers: authHeaders() });
      const data = res.ok ? unwrapLookupItems(await res.json()) : [];
      setVehicleWarehouses((Array.isArray(data) ? data : []).filter(isVehicleWarehouse));
    } catch { setVehicleWarehouses([]); }
  }, []);

  useEffect(() => { if (isNew) fetchVehicleWarehouses(); }, [isNew, fetchVehicleWarehouses]);

  const loadSnapshot = useCallback(async (warehouseId) => {
    if (!warehouseId) { setSnapshot({ items: [], totalCases: 0, totalUnits: 0, warehouseName: '', warehouseCode: '' }); return; }
    setSnapshotLoading(true);
    try {
      const res = await fetch(`${API_BASE}/warehouse-stock?warehouseId=${encodeURIComponent(warehouseId)}`, { headers: authHeaders() });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.message || payload?.Message || 'Stok alınamadı');
      const data = pick(payload, 'Data', 'data') ?? payload;
      setSnapshot({
        items: pick(data, 'items', 'Items') ?? [],
        totalCases: pick(data, 'totalCases', 'TotalCases') ?? 0,
        totalUnits: pick(data, 'totalUnits', 'TotalUnits') ?? 0,
        warehouseName: pick(data, 'warehouseName', 'WarehouseName') ?? '',
        warehouseCode: pick(data, 'warehouseCode', 'WarehouseCode') ?? '',
      });
    } catch (err) {
      showToast('error', err.message || 'Stok alınamadı');
      setSnapshot({ items: [], totalCases: 0, totalUnits: 0, warehouseName: '', warehouseCode: '' });
    } finally {
      setSnapshotLoading(false);
    }
  }, [showToast]);

  const onWarehouseChange = useCallback((warehouseId) => {
    setCreateForm((f) => ({ ...f, warehouseId }));
    loadSnapshot(warehouseId);
  }, [loadSnapshot]);

  // Tek kayıt yükleme (salt-okunur): önce location.state.row ile başlık, sonra /:id/details ile satırlar.
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    setLoading(true);
    const row = location.state?.row;
    if (row) {
      setDetail({
        id: pick(row, 'id', 'Id') ?? id,
        noteNo: pick(row, 'noteNo', 'NoteNo') ?? '',
        plate: pick(row, 'plate', 'Plate') ?? '',
        sdmName: pick(row, 'sdmName', 'SdmName') ?? '',
        status: pick(row, 'status', 'Status') ?? '',
        deliveryDate: pick(row, 'deliveryDate', 'DeliveryDate') ?? '',
        printedAt: pick(row, 'printedAt', 'PrintedAt') ?? null,
        totalCases: pick(row, 'totalCases', 'TotalCases') ?? null,
        totalUnits: pick(row, 'totalUnits', 'TotalUnits') ?? null,
      });
      setAudit({ createdBy: pick(row, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(row, 'createdDate', 'CreatedDate'), updatedBy: pick(row, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(row, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    }
    try {
      const res = await fetch(`${API_BASE}/${id}/details`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Satırlar alınamadı (${res.status})`);
      const payload = await res.json();
      const data = pick(payload, 'Data', 'data') ?? payload;
      setDetailLines(pick(data, 'items', 'Items') ?? []);
    } catch (err) {
      showToast('error', err.message || 'Satırlar alınamadı');
      setDetailLines([]);
    } finally {
      setLoading(false);
    }
  }, [id, isNew, location.state, showToast]);

  useEffect(() => { load(); }, [load]);

  // andNew: kaydet + yazdır ama listeye dönme — formu sıfırlayıp boş yeni kayda hazırlan. Sadece yeni kayıt modunda.
  const persist = async ({ andNew = false } = {}) => {
    if (!createForm.warehouseId) { showToast('error', 'Araç deposu seçiniz'); return; }
    if (!snapshot.items.length) { showToast('error', 'Seçilen depoda stoğu olan ürün yok'); return; }
    setCreating(true);
    try {
      const body = {
        warehouseId: createForm.warehouseId,
        date: createForm.date || null,
        documentDate: createForm.documentDate ? toDateTimeOffset(createForm.documentDate) : null,
        shipmentDate: createForm.shipmentDate ? toDateTimeOffset(createForm.shipmentDate) : null,
        description: createForm.description || null,
        matbuNo: createForm.matbuNo || null,
      };
      const res = await fetch(API_BASE, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.message || payload?.Message || 'İrsaliye kaydedilemedi');
      const result = pick(payload, 'Data', 'data') ?? payload;
      const noteNo = pick(result, 'noteNo', 'NoteNo') ?? '';
      showToast('success', `Sevk İrsaliyesi oluşturuldu${noteNo ? ` (${noteNo})` : ''}`);
      // Kaydet-sonrası yazdırma akışı KORUNUR.
      printDeliveryNote({
        noteNo,
        warehouseName: snapshot.warehouseName,
        warehouseCode: snapshot.warehouseCode,
        date: createForm.date,
        documentDate: createForm.documentDate,
        shipmentDate: createForm.shipmentDate,
        description: createForm.description,
        matbuNo: createForm.matbuNo,
        lines: snapshot.items,
        totalCases: snapshot.totalCases,
        totalUnits: snapshot.totalUnits,
      });
      if (andNew) {
        setCreateForm(emptyCreateForm());
        setSnapshot({ items: [], totalCases: 0, totalUnits: 0, warehouseName: '', warehouseCode: '' });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      setTimeout(() => navigate('/delivery-notes'), 400);
    } catch (err) {
      showToast('error', err.message || 'İrsaliye kaydedilemedi');
    } finally {
      setCreating(false);
    }
  };

  const handleSave = () => persist();

  const warehouseOptions = vehicleWarehouses.map((w) => {
    const wid = w.id ?? w.Id;
    const code = w.textId ?? w.TextId ?? '';
    const name = w.name ?? w.Name ?? '';
    return { value: wid, label: [code, name].filter(Boolean).join(' - ') };
  });

  const title = isNew
    ? 'Yeni Sevk İrsaliyesi'
    : (detail.noteNo ? `İrsaliye ${detail.noteNo}` : 'Sevk İrsaliyesi Detayı');

  const statusBadge = !isNew && detail.status ? statusBadgeFor(detail.status) : null;

  const actions = (
    <>
      <button className="dps-btn ghost" onClick={() => navigate('/delivery-notes')} disabled={creating}>
        {isNew ? 'İptal' : 'Geri'}
      </button>
      {isNew && canSave && (
        <SplitButton
          label={creating ? 'Kaydediliyor…' : 'Kaydet ve Yazdır'}
          icon="check"
          onClick={handleSave}
          disabled={creating || !createForm.warehouseId || snapshot.items.length === 0}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet + yazdır, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      )}
    </>
  );

  return (
    <DetailShell
      crumb={[
        { label: 'Sevk İrsaliyeleri', to: '/delivery-notes' },
        { label: isNew ? 'Yeni Sevk İrsaliyesi' : 'İrsaliye Detayı', current: true },
      ]}
      status={statusBadge}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'DeliveryNoteHeader', entityId: id }}
      loading={loading}
    >
      {isNew ? (
        <div className="dps-form">
          <Section icon="layers" title="İrsaliye Bilgileri" subtitle="Araç deposu ve belge tarihleri">
            <Grid cols={2}>
              <FieldCustom label="Araç Deposu" req>
                <div className="dps-ff-input">
                  <select value={createForm.warehouseId} onChange={(e) => onWarehouseChange(e.target.value)}>
                    <option value="">Araç deposu seçiniz</option>
                    {warehouseOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <span className="dps-ff-chev"><Icon name="chevD" size={16} /></span>
                </div>
                {vehicleWarehouses.length === 0 && (
                  <span style={{ fontSize: 12, color: '#92400e' }}>Araç tipinde depo bulunamadı.</span>
                )}
              </FieldCustom>
              <FieldText form={createForm} set={setCreate} k="matbuNo" label="Matbu No" />
              <FieldDate form={createForm} set={setCreate} k="date" label="Tarih" />
              <FieldDate form={createForm} set={setCreate} k="documentDate" label="Belge Tarihi" />
              <FieldDate form={createForm} set={setCreate} k="shipmentDate" label="Sevk Tarihi" />
              <FieldText form={createForm} set={setCreate} k="description" label="Açıklama" span />
            </Grid>
          </Section>

          <Section
            icon="box"
            title="Kalemler (deponun anlık stoğu — değiştirilemez)"
            subtitle="Kaydedildiğinde bu miktarlar araç deposundan çıkış olarak düşülür ve belge yazdırılır."
          >
            {snapshotLoading ? (
              <div className="dps-empty"><span>Yükleniyor…</span></div>
            ) : snapshot.items.length === 0 ? (
              <div className="dps-empty">
                <Icon name="box" size={34} />
                <p>{createForm.warehouseId ? 'Bu depoda stoğu olan ürün yok' : 'Önce bir araç deposu seçiniz'}</p>
                <span>Araç deposu seçildiğinde anlık stok kalemleri burada listelenir.</span>
              </div>
            ) : (
              <>
                <table className="dps-table">
                  <thead>
                    <tr>
                      <th>Ürün Kodu</th><th>Ürün Adı</th>
                      <th style={{ textAlign: 'right' }}>Koli</th>
                      <th style={{ textAlign: 'right' }}>Adet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {snapshot.items.map((l, idx) => (
                      <tr key={(l.productId ?? l.ProductId) || idx}>
                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{l.productCode ?? l.ProductCode}</td>
                        <td><strong>{l.productName ?? l.ProductName}</strong></td>
                        <td style={{ textAlign: 'right' }}>{l.cases ?? l.Cases ?? 0}</td>
                        <td style={{ textAlign: 'right' }}>{l.units ?? l.Units ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end', gap: 24, fontSize: 13, color: 'var(--text-secondary)' }}>
                  <span>Toplam Koli: <strong style={{ color: 'var(--text-primary)' }}>{snapshot.totalCases}</strong></span>
                  <span>Toplam Adet: <strong style={{ color: 'var(--text-primary)' }}>{snapshot.totalUnits}</strong></span>
                </div>
              </>
            )}
          </Section>
        </div>
      ) : (
        <div className="dps-form">
          <Section icon="layers" title="İrsaliye Bilgileri" subtitle="Sevk irsaliyesi başlık bilgileri (salt-okunur)">
            <Grid cols={2}>
              <FieldCustom label="İrsaliye No">
                <div className="dps-ff-input"><span style={ROVAL}>{detail.noteNo || '(Henüz basılmadı)'}</span></div>
              </FieldCustom>
              <FieldCustom label="Durum">
                <div style={{ paddingTop: 4 }}>{statusBadgeFor(detail.status)}</div>
              </FieldCustom>
              <FieldCustom label="Plaka">
                <div className="dps-ff-input"><span style={ROVAL}>{detail.plate || '-'}</span></div>
              </FieldCustom>
              <FieldCustom label="SDM">
                <div className="dps-ff-input"><span style={ROVAL}>{detail.sdmName || '-'}</span></div>
              </FieldCustom>
              <FieldCustom label="Sevkiyat Tarihi">
                <div className="dps-ff-input"><span style={ROVAL}>{detail.deliveryDate || '-'}</span></div>
              </FieldCustom>
              <FieldCustom label="Basım Zamanı">
                <div className="dps-ff-input"><span style={ROVAL}>{formatDateTime(detail.printedAt)}</span></div>
              </FieldCustom>
              <FieldCustom label="Toplam Koli">
                <div className="dps-ff-input"><span style={ROVAL}>{detail.totalCases ?? '-'}</span></div>
              </FieldCustom>
              <FieldCustom label="Toplam Adet">
                <div className="dps-ff-input"><span style={ROVAL}>{detail.totalUnits ?? '-'}</span></div>
              </FieldCustom>
            </Grid>
          </Section>

          <Section icon="box" title={`Satırlar${detailLines ? ` (${detailLines.length})` : ''}`} subtitle="İrsaliye kalemleri (salt-okunur)">
            {detailLines == null ? (
              <div className="dps-empty"><span>Yükleniyor…</span></div>
            ) : detailLines.length === 0 ? (
              <div className="dps-empty"><Icon name="box" size={34} /><p>Satır yok</p></div>
            ) : (
              <table className="dps-table">
                <thead>
                  <tr>
                    <th>Ürün</th>
                    <th style={{ textAlign: 'right' }}>Koli</th>
                    <th style={{ textAlign: 'right' }}>Adet</th>
                    <th>Seri No</th>
                  </tr>
                </thead>
                <tbody>
                  {detailLines.map((line, idx) => (
                    <tr key={pick(line, 'id', 'Id') || idx}>
                      <td>
                        <strong>{pick(line, 'productName', 'ProductName') || '-'}</strong>
                        {pick(line, 'productCode', 'ProductCode') && (
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{pick(line, 'productCode', 'ProductCode')}</div>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>{pick(line, 'cases', 'Cases') ?? '-'}</td>
                      <td style={{ textAlign: 'right' }}>{pick(line, 'units', 'Units') ?? '-'}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{pick(line, 'serialNo', 'SerialNo') || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>
        </div>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default DeliveryNoteDetailPage;
